import React, { useMemo, useState } from "react";

import { difference, orderBy } from "lodash";
import { useParams } from "react-router-dom";

import useAllAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useAllAccounts";
import useDeleteGoalBasedFinancialPlanExpense from "@fartherfinance/frontend/api/FinancialPlans/expenses/hooks/useDeleteGoalBasedFinancialPlanExpense";
import usePatchGoalBasedFinancialPlanExpense from "@fartherfinance/frontend/api/FinancialPlans/expenses/hooks/usePatchGoalBasedFinancialPlanExpense";
import usePostGoalBasedFinancialPlanExpense from "@fartherfinance/frontend/api/FinancialPlans/expenses/hooks/usePostGoalBasedFinancialPlanExpense";
import {
  GoalBasedExpensePeriod,
  GoalBasedExpensePeriodSchema,
  GoalBasedExpenseType,
  GoalBasedExpenseTypeSchema,
} from "@fartherfinance/frontend/api/FinancialPlans/expenses/types";
import {
  GoalBasedFinancialPlanExpenseId,
  GoalBasedFinancialPlanId,
} from "@fartherfinance/frontend/api/FinancialPlans/ids";
import { GoalBasedFinancialPlanSingleResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { GoalOwnerFieldType } from "../Goals/Forms/types";
import { AddedAccountOption } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Assets/Types";
import FilledExpenseCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Expenses/FilledExpenseCard/FilledExpenseCard";
import CardsRow from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/CardsRow/CardsRow";
import {
  defaultPlanDateFieldValues,
  OwnerField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/constants";
import EmptyCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/EmptyCard/EmptyCard";
import { getDateRangeStringFromPlanDates } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/helpers/getDateRangeStringFromPlanDates";
import { getInitialPlanDateFieldValuesForForm } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/helpers/getInitialPlanDateFieldValuesForForm";
import { ClientAndSpouseDetails } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/hooks/useGetClientAndSpouseDetails";
import {
  AnnualIncreaseAmountField,
  AnnualIncreaseTypeField,
  annualIncreaseTypeOptions,
  CustomInflation,
  defaultAnnualIncreaseFieldValues,
  defaultAnnualIncreaseTypeField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/MultiDropdown/AnnualIncrease/AnnualIncrease";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import MenuOptionsWithCheckIfSelected from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Text from "@src/sharedComponents/Typography/Text";
import getEntries from "@src/utils/entries";
import { formatExternalAccountName } from "@src/utils/ExternalAccount/formatExternalAccountName";
import { getExternalAccountName } from "@src/utils/ExternalAccount/getExternalAccountName";
import toUSD from "@src/utils/toUSD";

import AlimonyExpenseFormDrawer from "./Forms/Alimony";
import DebtExpenseFormDrawer from "./Forms/Debt";
import HealthcareExpenseFormDrawer from "./Forms/Healthcare";
import HealthcareExpenseOtherFormDrawer from "./Forms/HealthcareOther";
import LivingExpenseFormDrawer from "./Forms/Living";
import OtherExpenseFormDrawer from "./Forms/Other";
import TaxExpenseFormDrawer from "./Forms/Taxes";

import styles from "./Expenses.module.css";

const ExpenseMenuMap: Record<GoalBasedExpenseType, string> = {
  SimpleLiving: "OMIT",
  DetailedLiving: "OMIT",
  Alimony: "Alimony",
  DebtPayment: "Debt",
  Medical: "Healthcare",
  Other: "Other",
  TaxesAndFees: "Taxes and fees",
};

const expenseMenuEntries = getEntries(ExpenseMenuMap);

const expenseMenuOptions = orderBy(
  expenseMenuEntries
    .filter(([_, value]) => value !== "OMIT")
    .map(([key, value]) => ({
      id: key,
      label: value,
    })),
  [(g) => g.label],
  ["asc"]
);
interface Props {
  singleGoalBasedFinancialPlan: GoalBasedFinancialPlanSingleResponse;
  ownerOptions: GoalOwnerFieldType[];
  clientSpouseData: ClientAndSpouseDetails;
}

const FinancialPlanExpenses: React.FC<Props> = ({
  singleGoalBasedFinancialPlan,
  clientSpouseData,
  ownerOptions,
}) => {
  const { clientId, goalBasedFinancialPlanId } = useParams<{
    clientId: ClientId;
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  }>();

  const auth = useRequestAuth();

  const allAccounts = useAllAccounts(clientId, auth);

  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState<
    GoalBasedFinancialPlanExpenseId | undefined
  >(undefined);

  const selectedExpense = useMemo(
    () =>
      singleGoalBasedFinancialPlan.expenses.find(
        (exp) => exp.expenseId === selectedExpenseId
      ),
    [selectedExpenseId, singleGoalBasedFinancialPlan.expenses]
  );

  const linkedGoalExpense = useMemo(() => {
    return singleGoalBasedFinancialPlan.goals.find(
      (g) => g.goalId === selectedExpense?.linkedGoalId
    );
  }, [selectedExpense, singleGoalBasedFinancialPlan]);

  const [drawer, setDrawer] = React.useState<
    [GoalBasedExpenseType, GoalBasedExpensePeriod] | undefined
  >(undefined);

  const healthcareExpenseBeforeRetirement =
    singleGoalBasedFinancialPlan.expenses.find(
      (exp) =>
        exp.expenseType === GoalBasedExpenseTypeSchema.enum.Medical &&
        exp.period === GoalBasedExpensePeriodSchema.enum.BeforeRetirement
    );

  const healthcareExpenseAfterRetirement =
    singleGoalBasedFinancialPlan.expenses.find(
      (exp) =>
        exp.expenseType === GoalBasedExpenseTypeSchema.enum.Medical &&
        exp.period === GoalBasedExpensePeriodSchema.enum.AfterRetirement
    );

  const livingExpenseBeforeRetirement =
    singleGoalBasedFinancialPlan.expenses.find(
      (exp) =>
        (exp.expenseType === GoalBasedExpenseTypeSchema.enum.SimpleLiving ||
          exp.expenseType === GoalBasedExpenseTypeSchema.enum.DetailedLiving) &&
        exp.period === GoalBasedExpensePeriodSchema.enum.BeforeRetirement
    );

  const livingExpenseAfterRetirement =
    singleGoalBasedFinancialPlan.expenses.find(
      (exp) =>
        (exp.expenseType === GoalBasedExpenseTypeSchema.enum.SimpleLiving ||
          exp.expenseType === GoalBasedExpenseTypeSchema.enum.DetailedLiving) &&
        exp.period === GoalBasedExpensePeriodSchema.enum.AfterRetirement
    );

  const otherExpenses = difference(singleGoalBasedFinancialPlan.expenses, [
    livingExpenseBeforeRetirement,
    livingExpenseAfterRetirement,
    healthcareExpenseBeforeRetirement,
    healthcareExpenseAfterRetirement,
  ]);

  const {
    mutateAsync: postGoalBasedFinancialPlanExpense,
    isLoading: isPosting,
  } = usePostGoalBasedFinancialPlanExpense(auth);

  const {
    mutateAsync: patchGoalBasedFinancialPlanExpense,
    isLoading: isPatching,
  } = usePatchGoalBasedFinancialPlanExpense(auth);

  const {
    mutateAsync: deleteGoalBasedFinancialPlanExpense,
    isLoading: isDeleting,
  } = useDeleteGoalBasedFinancialPlanExpense(auth);

  const isLoading = isPosting || isPatching || isDeleting;

  const handleOnCloseDrawer = () => {
    setSelectedExpenseId(undefined);
    setDrawer(undefined);
  };

  const { client, spouse } = clientSpouseData;

  const spouseHasRetrementHorizon =
    singleGoalBasedFinancialPlan.retirementHorizon.some(
      (r) => spouse?.clientId === r.owner
    );

  const addedAccountOptions: AddedAccountOption[] = useMemo(() => {
    if (allAccounts.isLoading || allAccounts.hasError) {
      return [];
    }

    return singleGoalBasedFinancialPlan.accounts.map((acc) => {
      let label = undefined;
      if (acc.accountType === "FartherManagedAccount") {
        const account = (allAccounts.data.fartherAccounts ?? []).find(
          (el) => el.virtualAccountId === acc.accountId
        );
        if (account === undefined) {
          throw new Error(`Managed account: ${acc.accountId} not found`);
        }

        label = account.accountDetails.displayName;
      } else if (acc.accountType === "ManuallyTrackedAccount") {
        const account = (allAccounts.data.manuallyTrackedAccounts ?? []).find(
          (el) => el.id === acc.accountId
        );
        if (account === undefined) {
          throw new Error(`Manual account: ${acc.accountId} not found`);
        }

        label = account.assetName;
      } else {
        const account = (allAccounts.data.externalAccounts ?? []).find(
          (el) => el.accountId === acc.accountId
        );
        if (account === undefined) {
          throw new Error(`External account: ${acc.accountId} not found`);
        }

        label = formatExternalAccountName(getExternalAccountName(account), 14);
      }

      return {
        label,
        value: acc.accountId,
      };
    });
  }, [singleGoalBasedFinancialPlan, allAccounts]);

  const initialPlanDateOwnerAndAnnualIncreaseFields = useMemo(() => {
    const clientName = client.fullName ?? "";
    const spouseName = spouse?.fullName ?? "Spouse";

    let ownerField = { label: clientName, value: clientId };

    if (selectedExpense === undefined) {
      return {
        [OwnerField]: ownerField,
        ...defaultAnnualIncreaseFieldValues,
        ...defaultPlanDateFieldValues,
      };
    }

    if (
      selectedExpense.owner !== undefined &&
      selectedExpense.owner !== clientId
    ) {
      ownerField = { label: spouseName, value: selectedExpense.owner };
    }

    const annualIncreaseTypeField = annualIncreaseTypeOptions.find(
      (el) => el.value === selectedExpense.annualIncrease?.type
    ) ?? { ...defaultAnnualIncreaseTypeField };

    const annualIncreaseAmountField =
      selectedExpense.annualIncrease?.type === CustomInflation
        ? selectedExpense.annualIncrease?.value.toString()
        : undefined;

    return {
      ...getInitialPlanDateFieldValuesForForm({
        clientId,
        retirementHorizons: singleGoalBasedFinancialPlan.retirementHorizon,
        existingCategoryElement: selectedExpense,
      }),
      [OwnerField]: ownerField,
      [AnnualIncreaseTypeField]: annualIncreaseTypeField,
      [AnnualIncreaseAmountField]: annualIncreaseAmountField,
    };
  }, [
    clientId,
    selectedExpense,
    singleGoalBasedFinancialPlan.retirementHorizon,
    client,
    spouse,
  ]);

  return (
    <>
      <PageHero
        className={styles.pageHero}
        innerClassName={styles.pageHeroInner}
        start={
          <TitleBlock
            title="Expenses"
            subTitle="Expenses are split between before and during retirement, as they can change quite a bit."
          />
        }
        end={
          <CustomDropdownBase
            className={styles.dropdownBase}
            curItem={{
              value: "Add Expense",
              displayComponent: (
                <Text variant={"body"} color={"text"}>
                  Add Expense
                </Text>
              ),
            }}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            width={240}
            isDisabled={false}
            hideIcon={true}
          >
            <MenuOptionsWithCheckIfSelected
              onSelect={(el) => {
                setSelectedExpenseId(undefined);
                setDrawer([el.id, GoalBasedExpensePeriodSchema.enum.Other]);
                setShowDropdown(false);
              }}
              curItemName={"Add Expense"}
              options={expenseMenuOptions}
            />
          </CustomDropdownBase>
        }
        endClassName={styles.pageHeroEnd}
      />

      <BodyContentFormatter>
        <CardsRow>
          {livingExpenseBeforeRetirement === undefined && (
            <EmptyCard
              onAdd={() =>
                setDrawer([
                  GoalBasedExpenseTypeSchema.enum.SimpleLiving,
                  GoalBasedExpensePeriodSchema.enum.BeforeRetirement,
                ])
              }
              heading="Living Expenses Before Retirement"
              text={
                spouseHasRetrementHorizon ? "Shared" : client.fullName ?? ""
              }
            />
          )}

          {livingExpenseBeforeRetirement !== undefined && (
            <FilledExpenseCard
              heading={livingExpenseBeforeRetirement.name}
              hasAccounts={addedAccountOptions.length > 0}
              onClick={() => {
                setSelectedExpenseId(livingExpenseBeforeRetirement.expenseId);
                setDrawer([
                  GoalBasedExpenseTypeSchema.enum.SimpleLiving,
                  GoalBasedExpensePeriodSchema.enum.BeforeRetirement,
                ]);
              }}
              text={
                spouseHasRetrementHorizon ? "Shared" : client.fullName ?? ""
              }
              title={toUSD(parseFloat(livingExpenseBeforeRetirement.amount), {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
              titleRight="Before Retirement"
            />
          )}

          {healthcareExpenseBeforeRetirement === undefined && (
            <EmptyCard
              onAdd={() =>
                setDrawer([
                  GoalBasedExpenseTypeSchema.enum.Medical,
                  GoalBasedExpensePeriodSchema.enum.BeforeRetirement,
                ])
              }
              heading="Healthcare Expenses Before Retirement"
              text={
                spouseHasRetrementHorizon ? "Shared" : client.fullName ?? ""
              }
            />
          )}

          {healthcareExpenseBeforeRetirement !== undefined && (
            <FilledExpenseCard
              heading={healthcareExpenseBeforeRetirement.name}
              hasAccounts={addedAccountOptions.length > 0}
              onClick={() => {
                setSelectedExpenseId(
                  healthcareExpenseBeforeRetirement.expenseId
                );
                setDrawer([
                  GoalBasedExpenseTypeSchema.enum.Medical,
                  GoalBasedExpensePeriodSchema.enum.BeforeRetirement,
                ]);
              }}
              text={
                spouseHasRetrementHorizon ? "Shared" : client.fullName ?? ""
              }
              title={toUSD(
                parseFloat(healthcareExpenseBeforeRetirement.amount),
                { minimumFractionDigits: 0, maximumFractionDigits: 0 }
              )}
              titleRight="Before Retirement"
            />
          )}
        </CardsRow>

        <CardsRow>
          {livingExpenseAfterRetirement === undefined && (
            <EmptyCard
              onAdd={() =>
                setDrawer([
                  GoalBasedExpenseTypeSchema.enum.SimpleLiving,
                  GoalBasedExpensePeriodSchema.enum.AfterRetirement,
                ])
              }
              heading="Living Expenses After Retirement"
              text={
                spouseHasRetrementHorizon ? "Shared" : client.fullName ?? ""
              }
            />
          )}

          {livingExpenseAfterRetirement !== undefined && (
            <FilledExpenseCard
              heading={livingExpenseAfterRetirement.name}
              hasAccounts={addedAccountOptions.length > 0}
              onClick={() => {
                setSelectedExpenseId(livingExpenseAfterRetirement.expenseId);
                setDrawer([
                  GoalBasedExpenseTypeSchema.enum.SimpleLiving,
                  GoalBasedExpensePeriodSchema.enum.AfterRetirement,
                ]);
              }}
              text={
                spouseHasRetrementHorizon ? "Shared" : client.fullName ?? ""
              }
              title={toUSD(parseFloat(livingExpenseAfterRetirement.amount), {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
              titleRight="After Retirement"
            />
          )}

          {healthcareExpenseAfterRetirement === undefined && (
            <EmptyCard
              onAdd={() =>
                setDrawer([
                  GoalBasedExpenseTypeSchema.enum.Medical,
                  GoalBasedExpensePeriodSchema.enum.AfterRetirement,
                ])
              }
              heading="Healthcare Expenses After Retirement"
              text={
                spouseHasRetrementHorizon ? "Shared" : client.fullName ?? ""
              }
            />
          )}

          {healthcareExpenseAfterRetirement !== undefined && (
            <FilledExpenseCard
              heading={healthcareExpenseAfterRetirement.name}
              hasAccounts={addedAccountOptions.length > 0}
              onClick={() => {
                setSelectedExpenseId(
                  healthcareExpenseAfterRetirement.expenseId
                );
                setDrawer([
                  GoalBasedExpenseTypeSchema.enum.Medical,
                  GoalBasedExpensePeriodSchema.enum.AfterRetirement,
                ]);
              }}
              text={
                spouseHasRetrementHorizon ? "Shared" : client.fullName ?? ""
              }
              title={toUSD(
                parseFloat(healthcareExpenseAfterRetirement.amount),
                { minimumFractionDigits: 0, maximumFractionDigits: 0 }
              )}
              titleRight="After Retirement"
            />
          )}
        </CardsRow>

        <CardsRow maxColumns={2}>
          {orderBy(otherExpenses, [(e) => e?.expenseType], ["asc"]).map(
            (expense) => {
              if (expense === undefined) {
                return null;
              }

              return (
                <FilledExpenseCard
                  key={expense.expenseId}
                  hasAccounts={expense.paymentAccountId !== null}
                  heading={ExpenseMenuMap[expense.expenseType]}
                  onClick={() => {
                    setSelectedExpenseId(expense.expenseId);
                    setDrawer([expense.expenseType, expense.period]);
                  }}
                  text={expense.name}
                  title={toUSD(parseFloat(expense.amount), {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                  titleRight={getDateRangeStringFromPlanDates({
                    startDate:
                      expense.startDate === null
                        ? undefined
                        : expense.startDate,
                    endDate:
                      expense.endDate === null ? undefined : expense.endDate,
                    clientAndSpouseDetails: clientSpouseData,
                    repeats: expense.repeats,
                    owner: expense.owner,
                  })}
                />
              );
            }
          )}
        </CardsRow>

        <AlimonyExpenseFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.Alimony &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.Other
          }
          expenseToEdit={selectedExpense}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          ownerOptions={ownerOptions}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          addedAccountOptions={addedAccountOptions}
          initialPlanDateOwnerAndAnnualIncreaseFields={
            initialPlanDateOwnerAndAnnualIncreaseFields
          }
        />

        <DebtExpenseFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.DebtPayment &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.Other
          }
          expenseToEdit={selectedExpense}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          ownerOptions={ownerOptions}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          addedAccountOptions={addedAccountOptions}
          initialPlanDateOwnerAndAnnualIncreaseFields={
            initialPlanDateOwnerAndAnnualIncreaseFields
          }
        />

        <HealthcareExpenseOtherFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.Medical &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.Other
          }
          expenseToEdit={selectedExpense}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          ownerOptions={ownerOptions}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          addedAccountOptions={addedAccountOptions}
          initialPlanDateOwnerAndAnnualIncreaseFields={
            initialPlanDateOwnerAndAnnualIncreaseFields
          }
        />

        <HealthcareExpenseFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.Medical &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.BeforeRetirement
          }
          expensePeriod="BeforeRetirement"
          expenseToEdit={healthcareExpenseBeforeRetirement}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          owner={{
            label: client.fullName,
            value: clientId,
          }}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          addedAccountOptions={addedAccountOptions}
          initialPlanDateOwnerAndAnnualIncreaseFields={
            initialPlanDateOwnerAndAnnualIncreaseFields
          }
        />

        <HealthcareExpenseFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.Medical &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.AfterRetirement
          }
          expensePeriod="AfterRetirement"
          expenseToEdit={healthcareExpenseAfterRetirement}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          owner={{
            label: client.fullName,
            value: clientId,
          }}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          addedAccountOptions={addedAccountOptions}
          initialPlanDateOwnerAndAnnualIncreaseFields={
            initialPlanDateOwnerAndAnnualIncreaseFields
          }
        />

        <LivingExpenseFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.SimpleLiving &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.BeforeRetirement
          }
          expensePeriod="BeforeRetirement"
          expenseToEdit={livingExpenseBeforeRetirement}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          owner={{
            label: client.fullName,
            value: clientId,
          }}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          addedAccountOptions={addedAccountOptions}
        />

        <LivingExpenseFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.SimpleLiving &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.AfterRetirement
          }
          expensePeriod={"AfterRetirement"}
          expenseToEdit={livingExpenseAfterRetirement}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          owner={{
            label: client.fullName,
            value: clientId,
          }}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          addedAccountOptions={addedAccountOptions}
        />

        <OtherExpenseFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.Other &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.Other
          }
          expenseToEdit={selectedExpense}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          ownerOptions={ownerOptions}
          linkedGoalExpense={linkedGoalExpense}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          addedAccountOptions={addedAccountOptions}
          initialPlanDateOwnerAndAnnualIncreaseFields={
            initialPlanDateOwnerAndAnnualIncreaseFields
          }
        />

        <TaxExpenseFormDrawer
          deleteExpense={deleteGoalBasedFinancialPlanExpense}
          drawerOpen={
            drawer?.[0] === GoalBasedExpenseTypeSchema.enum.TaxesAndFees &&
            drawer?.[1] === GoalBasedExpensePeriodSchema.enum.Other
          }
          expenseToEdit={selectedExpense}
          goalBasedFinancialPlanId={goalBasedFinancialPlanId}
          isLoading={isLoading}
          onClose={handleOnCloseDrawer}
          ownerOptions={ownerOptions}
          patchExpense={patchGoalBasedFinancialPlanExpense}
          postExpense={postGoalBasedFinancialPlanExpense}
          retirementHorizons={singleGoalBasedFinancialPlan.retirementHorizon}
          hideSpouseOptions={!spouseHasRetrementHorizon}
          addedAccountOptions={addedAccountOptions}
          initialPlanDateOwnerAndAnnualIncreaseFields={
            initialPlanDateOwnerAndAnnualIncreaseFields
          }
        />
      </BodyContentFormatter>
    </>
  );
};

export default FinancialPlanExpenses;
